import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { useI18n } from "i18n";
import { selectMultipleCompanyView } from "modules/company/redux/selectors";
import { selectUser } from "modules/auth/redux/selectors";
import ProfileMenu from "modules/userProfileMenu/ProfileMenu";
import TrialBanner from "modules/trial/TrialBanner";
import { AppBar } from "@remo-co/ui-core/src/components/AppBar";
import { Toolbar } from "@remo-co/ui-core/src/components/Toolbar";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { maxContentWidth } from "@remo-co/ui-core/src/constants";
import ChangelogButton from "./ChangelogButton";
import { useStyles } from "./styles";

interface IAppBarProps {
  hideSigInButton?: boolean;
  hideTitle?: boolean;
  title?: string;
  rightAdornment?: ReactNode;
  noTitlePadding?: boolean;
  logoImage?: ReactNode;
}

const CHANGELOG_PAGES = ["/my-events", "/account-settings"];

const AppBarContainer = ({
  rightAdornment,
  title,
  hideTitle,
  hideSigInButton,
  noTitlePadding,
  logoImage,
}: IAppBarProps) => {
  const location = useLocation();
  const user = useSelector(selectUser);
  const multipleCompanyView: boolean = useSelector(selectMultipleCompanyView);
  const path = window.location.pathname;
  const { t } = useI18n(["common", "menu"]);
  const styles = useStyles();

  return (
    <div className={styles.appBarContainer}>
      <AppBar position="static" className={styles.header}>
        <Toolbar disableGutters>
          <Box
            boxSizing="border-box"
            maxWidth={maxContentWidth}
            mx="auto"
            px={3}
            width="100%"
            className={styles.toolbarBox}
          >
            <div className={styles.logoContainer}>
              {!hideTitle && !logoImage ? (
                <Typography
                  variant="h2"
                  className={classNames(styles.title, {
                    [styles.noTitlePadding]: noTitlePadding,
                  })}
                >
                  {title || "Remo"}
                </Typography>
              ) : null}
              {logoImage && logoImage}
            </div>
            <div
              className={`${styles.rightAdornment} ${
                multipleCompanyView ? styles.rightmost : ""
              }`}
            >
              <TrialBanner />
              {CHANGELOG_PAGES.some((changelogPage) =>
                (location.pathname || "").startsWith(changelogPage),
              ) && <ChangelogButton />}
              {rightAdornment || (
                <div
                  className={
                    multipleCompanyView
                      ? styles.companyProfile
                      : styles.profilePicture
                  }
                >
                  {!user && !hideSigInButton && (
                    <Button<typeof Link>
                      variant="ghost"
                      color="dark"
                      component={Link}
                      to={`/signin?redirectTo=${path}`}
                      style={{ color: "initial", marginLeft: "-1rem" }}
                    >
                      {t("menu:signin")}
                    </Button>
                  )}
                  {user && <ProfileMenu contain />}
                </div>
              )}
            </div>
          </Box>
        </Toolbar>
      </AppBar>
    </div>
  );
};

export default AppBarContainer;
